import { useEffect, useState } from "react";
import { Loader2, Building2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/apiClient";
import { useAuth, hasRole } from "@/context/AuthContext";
import { PageHeader } from "@/components/dashboard/primitives";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import ImageUpload from "@/components/ImageUpload";

export default function OrgSettings() {
  const { role, refreshMe, activeMembership } = useAuth();
  const [form, setForm] = useState(null);
  const [logs, setLogs] = useState([]);
  const [saving, setSaving] = useState(false);
  const canEdit = hasRole(role, "org_admin");
  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  useEffect(() => {
    api.get("/org").then(({ data }) => setForm({
      name: data.name || "", logo: data.logo || null, website: data.website || "",
      contact_email: data.contact_email || "", description: data.description || "",
      plan: data.plan, status: data.status,
    })).catch(() => {});
    if (canEdit) api.get("/org/audit-logs").then(({ data }) => setLogs(data)).catch(() => {});
  }, [activeMembership?.org_id, canEdit]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/org", { name: form.name, logo: form.logo, website: form.website, contact_email: form.contact_email, description: form.description });
      toast.success("Organisation updated");
      await refreshMe();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (!form) return <div className="p-8 max-w-3xl"><Skeleton className="h-96 rounded-xl" /></div>;

  return (
    <div className="p-5 sm:p-8 max-w-3xl">
      <PageHeader title="Organisation settings" subtitle="Manage your organisation profile and branding." />

      <form onSubmit={save} className="rounded-xl border border-border bg-card p-6 space-y-5" data-testid="org-settings-form">
        <div className="flex items-center gap-4">
          <div className="w-24 shrink-0">
            <ImageUpload value={form.logo} onChange={set("logo")} label="Logo" testid="org-logo" aspect="aspect-square" />
          </div>
          <div className="flex-1">
            <Label>Organisation name</Label>
            <Input value={form.name} onChange={set("name")} required disabled={!canEdit} className="mt-1.5" data-testid="org-name" />
            <div className="mt-2 flex gap-2">
              <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-accent/10 text-accent capitalize">{form.plan} plan</span>
              <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-muted text-muted-foreground capitalize">{form.status}</span>
            </div>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label>Website</Label><Input value={form.website} onChange={set("website")} disabled={!canEdit} className="mt-1.5" placeholder="https://" /></div>
          <div><Label>Contact email</Label><Input value={form.contact_email} onChange={set("contact_email")} disabled={!canEdit} className="mt-1.5" /></div>
        </div>
        <div><Label>Description</Label><Textarea value={form.description} onChange={set("description")} disabled={!canEdit} rows={3} className="mt-1.5" /></div>
        {canEdit && <Button type="submit" disabled={saving} className="rounded-md" data-testid="org-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}</Button>}
      </form>

      {canEdit && (
        <div className="mt-8">
          <h2 className="font-display text-lg font-bold text-foreground mb-4">Activity log</h2>
          <div className="rounded-xl border border-border bg-card divide-y divide-border max-h-96 overflow-y-auto" data-testid="audit-logs">
            {logs.length === 0 ? <p className="p-4 text-sm text-muted-foreground">No activity recorded yet.</p> : logs.map((l) => (
              <div key={l.id} className="flex items-center justify-between p-3.5 text-sm">
                <span className="text-foreground"><span className="font-medium">{l.user_name || "Someone"}</span> <span className="text-muted-foreground">{l.action?.replace(/[._]/g, " ")}</span></span>
                <span className="text-xs text-muted-foreground">{l.created_at ? new Date(l.created_at).toLocaleString() : ""}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
